import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { toastDefs, toastDefsError } from "../../../../../../config/toastConfig";
import { IAnexo } from "../../../../../../utility/IAnexo";
import { truncarNomeArquivo } from "../../../../../../utility/byteSizeUtils";
import api from "../../../../../../services/api";
import ComponenteArquivos from "../../../../ComponenteArquivos";
import { ArquivoUploadInfo } from "../../../../ComponenteArquivos/utils";
import { calcularTamanhoTotalAnexos } from "../../../../ComponenteArquivosProposta/utils";
import { carregarArquivosOrientacao, enviarArquivoOrientacao, validarTamanhoArquivo } from "./utils";

interface ComponenteArquivosOrientacaoProps {
    idProposta: number;
    idOrientacao?: number;
    anexosIniciais?: IAnexo[];
    somenteLeitura?: boolean;
    aoAtualizarLista?: (anexos: IAnexo[]) => void;
    aoUploadingChange?: (isUploading: boolean) => void;
    sessionId?: string;
}

export default function ComponenteArquivosOrientacao({
    idProposta,
    idOrientacao,
    anexosIniciais,
    somenteLeitura,
    aoAtualizarLista,
    aoUploadingChange,
    sessionId
}: ComponenteArquivosOrientacaoProps) {
    const [arrastando, setArrastando] = useState(false);
    const [anexos, setAnexos] = useState<IAnexo[]>(anexosIniciais ?? []);
    const [carregando, setCarregando] = useState(false);
    const [uploads, setUploads] = useState<ArquivoUploadInfo[]>([]);
    const [baixando, setBaixando] = useState<number | null>(null);
    const ultimaRequisicaoRef = useRef<number>(0);
    const [todosAnexosDemanda, setTodosAnexosDemanda] = useState<IAnexo[]>([]);

    useEffect(() => {
        if (anexosIniciais) {
            setAnexos(anexosIniciais);
        }
    }, [anexosIniciais]);

    useEffect(() => {
        aoAtualizarLista?.(anexos);
    }, [anexos, aoAtualizarLista]);

    useEffect(() => {
        aoUploadingChange?.(uploads.length > 0);
    }, [uploads, aoUploadingChange]);

    useEffect(() => {
        const carregarTodosAnexosDemanda = async () => {
            if (!idProposta) return;
            try {
                const resposta = await api.get(`proposta/${idProposta}/anexos`);
                const anexosRetornados = resposta.data?.result || [];
                setTodosAnexosDemanda(anexosRetornados);
            } catch (_erro) {
                setTodosAnexosDemanda([]);
            }
        };
        carregarTodosAnexosDemanda();
    }, [idProposta]);

    useEffect(() => {
        const carregarArquivosIniciais = async () => {
            if (!idOrientacao) {
                return;
            }

            const requisicaoId = Date.now();
            ultimaRequisicaoRef.current = requisicaoId;

            setCarregando(true);

            try {
                const anexosConvertidos = await carregarArquivosOrientacao(idOrientacao, idProposta);
                if (ultimaRequisicaoRef.current !== requisicaoId) {
                    return;
                }
                setAnexos(anexosConvertidos);
            } catch (_erro) {
                if (ultimaRequisicaoRef.current === requisicaoId) {
                    toast.error("Erro ao buscar arquivos da orientação", toastDefsError);
                }
            } finally {
                if (ultimaRequisicaoRef.current === requisicaoId) {
                    setCarregando(false);
                }
            }
        };

        carregarArquivosIniciais();
    }, [idOrientacao, idProposta]);

    const tamanhoTotal = useMemo(() => calcularTamanhoTotalAnexos(anexos), [anexos]);

    const extrairNomeArquivo = (caminho: string): string => {
        return caminho.split('/').pop() || '';
    };

    const lidarComArquivos = async (files: FileList | null) => {
        if (!files || files.length === 0) {
            return;
        }

        const arquivosSelecionados = Array.from(files);

        const arquivoInvalido = arquivosSelecionados.find((arquivo) => !validarTamanhoArquivo(arquivo));
        if (arquivoInvalido) {
            toast.error(`Arquivo ${truncarNomeArquivo(arquivoInvalido.name)} excede limite de 50MB`, toastDefsError);
            return;
        }

        const caminhosOrientacaoAtual = new Set(
            anexos.map((anexo) => anexo.caminho).filter(Boolean)
        );

        const arquivosDuplicados = arquivosSelecionados.filter((arquivo) => {
            const nomeArquivo = arquivo.name;
            return todosAnexosDemanda.some((anexoDemanda) => {
                if (caminhosOrientacaoAtual.has(anexoDemanda.caminho)) return false;
                const nomeAnexo = anexoDemanda.caminho ? extrairNomeArquivo(anexoDemanda.caminho) : '';
                return nomeAnexo === nomeArquivo;
            }) || anexos.some((anexo) => {
                const nomeAnexo = anexo.caminho ? extrairNomeArquivo(anexo.caminho) : '';
                return nomeAnexo === nomeArquivo;
            });
        });

        if (arquivosDuplicados.length > 0) {
            arquivosDuplicados.forEach((arquivo) => {
                toast.error(`Arquivo "${truncarNomeArquivo(arquivo.name)}" já existe na demanda.`, toastDefsError);
            });
            return;
        }

        const novosUploads: ArquivoUploadInfo[] = arquivosSelecionados.map((arquivo) => ({
            arquivo,
            progresso: 0,
            status: "enviando"
        }));

        const indiceInicial = uploads.length;
        setUploads((anterior) => [...anterior, ...novosUploads]);

        const resultados = await Promise.allSettled(
            arquivosSelecionados.map((arquivo, indice) =>
                enviarArquivoOrientacao(
                    arquivo,
                    indiceInicial + indice,
                    idProposta,
                    setUploads,
                    undefined,
                    idOrientacao,
                    sessionId
                )
            )
        );

        const anexosBemSucedidos: IAnexo[] = resultados
            .filter((resultado) => resultado.status === "fulfilled" && resultado.value?.sucesso && resultado.value?.anexo)
            .map((resultado) => (resultado as PromiseFulfilledResult<any>).value.anexo);

        const sucessos = resultados.filter(
            (resultado) => resultado.status === "fulfilled" && resultado.value?.sucesso
        ).length;
        const falhas = resultados.filter(
            (resultado) => resultado.status === "fulfilled" && !resultado.value?.sucesso
        ).length;

        if (anexosBemSucedidos.length > 0) {
            setTimeout(() => {
                setAnexos((anteriores) => {
                    const caminhoExistentes = new Set(anteriores.map(a => a.caminho));
                    const anexosNovosFiltrados = anexosBemSucedidos.filter(anexo => !caminhoExistentes.has(anexo.caminho));
                    return [...anexosNovosFiltrados, ...anteriores];
                });
                setUploads((anteriores) => anteriores.filter((item) => !anexosBemSucedidos.some((anexo) => item.arquivo.name === anexo.caminho?.split('/').pop())));
            }, 100);
        }

        if (falhas === 0 && sucessos > 0) {
            toast.success(`${sucessos} arquivo(s) enviado(s) com sucesso`, toastDefs);
        } else if (sucessos > 0 && falhas > 0) {
            toast.success(`${sucessos} arquivo(s) enviado(s), ${falhas} falharam`, toastDefs);
        } else if (falhas > 0) {
            toast.error(`Falha ao enviar ${falhas} arquivo(s)`, toastDefsError);
        }
    };

    const aoArrastarSobre = (evento: React.DragEvent) => {
        evento.preventDefault();
        evento.stopPropagation();
        if (somenteLeitura) {
            return;
        }
        setArrastando(true);
    };

    const aoSairArrasto = (evento: React.DragEvent) => {
        evento.preventDefault();
        evento.stopPropagation();
        if (evento.currentTarget === evento.target) {
            setArrastando(false);
        }
    };

    const aoSoltarArquivos = (evento: React.DragEvent) => {
        evento.preventDefault();
        evento.stopPropagation();
        if (somenteLeitura) {
            return;
        }
        setArrastando(false);
        lidarComArquivos(evento.dataTransfer.files);
    };

    const cancelarUpload = (indice: number) => {
        setUploads((anteriores) => anteriores.filter((_, posicao) => posicao !== indice));
    };

    const tentarNovamenteUpload = async (indice: number) => {
        const dados = uploads[indice];
        if (!dados || dados.status !== "erro") {
            return;
        }

        setUploads((anteriores) =>
            anteriores.map((item, posicao) =>
                posicao === indice ? { ...item, status: "enviando", progresso: 0, erro: undefined } : item
            )
        );

        const resultado = await enviarArquivoOrientacao(
            dados.arquivo,
            indice,
            idProposta,
            setUploads,
            undefined,
            idOrientacao,
            sessionId
        );

        if (resultado?.sucesso && resultado?.anexo) {
            const anexoParaAdicionar = resultado.anexo;
            setTimeout(() => {
                setAnexos((anteriores) => {
                    const caminhoExistentes = new Set(anteriores.map(a => a.caminho));
                    if (!caminhoExistentes.has(anexoParaAdicionar.caminho)) {
                        return [anexoParaAdicionar, ...anteriores];
                    }
                    return anteriores;
                });
                setUploads((anteriores) => anteriores.filter((_, pos) => pos !== indice));
            }, 100);
        }
    };

    const baixarAnexo = async (anexo: IAnexo, indice: number) => {
        if (baixando === indice) {
            return;
        }

        if (!anexo.caminho) {
            toast.error("Caminho do arquivo não encontrado", toastDefsError);
            return;
        }

        try {
            setBaixando(indice);
            const nomeArquivo = anexo.caminho.split('/').pop() || '';
            const caminhoCodificado = anexo.caminho.split('/').map(segment => encodeURIComponent(segment)).join('/');
            await api.download(`getfile/${caminhoCodificado}`, nomeArquivo);
        } catch {
            toast.error("Erro ao fazer download do arquivo", toastDefsError);
        } finally {
            setBaixando(null);
        }
    };

    const [excluindo, setExcluindo] = useState<number | null>(null);

    const excluirAnexo = async (anexo: IAnexo, indice: number) => {
        if (excluindo === indice) {
            return;
        }

        if (!anexo.caminho) {
            toast.error("Caminho do arquivo não encontrado", toastDefsError);
            return;
        }

        try {
            setExcluindo(indice);

            const possuiIdOrientacaoArquivo = Boolean(anexo.id_proposta_arquivo);
            const arquivoTemporario = !possuiIdOrientacaoArquivo;

            if (arquivoTemporario) {
                const caminhoCodificado = anexo.caminho.split('/').map(s => encodeURIComponent(s)).join('/');
                await api.delete(`deletefile/${caminhoCodificado}`);
                setAnexos((anteriores) => anteriores.filter(a => a.caminho !== anexo.caminho));
                toast.success("Arquivo excluído com sucesso", toastDefs);
            } else {
                if (!idOrientacao) {
                    toast.error("ID da orientação não encontrado", toastDefsError);
                    return;
                }

                const idArquivo = anexo.id_proposta_arquivo;
                await api.delete(`orientacao/${idOrientacao}/arquivos/${idArquivo}`);
                setAnexos((anteriores) => anteriores.filter(a => a.id_proposta_arquivo !== idArquivo));
                toast.success("Arquivo excluído com sucesso", toastDefs);
            }
        } catch (erro: any) {
            const mensagem = erro?.response?.data?.message || "Erro ao excluir arquivo";
            toast.error(mensagem, toastDefsError);
        } finally {
            setExcluindo(null);
        }
    };

    return (
        <ComponenteArquivos
            titulo="Arquivos da Orientação"
            anexos={anexos}
            uploads={uploads}
            carregando={carregando}
            baixando={baixando}
            excluindo={excluindo}
            somenteLeitura={somenteLeitura}
            arrastando={arrastando}
            tamanhoTotal={tamanhoTotal}
            podeExcluir={!somenteLeitura}
            aoSelecionarArquivos={lidarComArquivos}
            aoTentarNovamente={tentarNovamenteUpload}
            aoCancelar={cancelarUpload}
            aoBaixar={baixarAnexo}
            aoExcluir={excluirAnexo}
            aoArrastarSobre={aoArrastarSobre}
            aoSairArrasto={aoSairArrasto}
            aoSoltarArquivos={aoSoltarArquivos}
        />
    );
}

